"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface PackageTier {
  id: string;
  name: string;
  badge?: string;
  tagline: string;
  pricingLabel: string;
  isPopular: boolean;
  features: string[];
}
const packages: PackageTier[] = [{
  id: "essential",
  name: "Essential SEO",
  tagline: "Foundational local visibility and technical baseline for local businesses.",
  pricingLabel: "Custom pricing — get a free quote",
  isPopular: false,
  features: ["Full Google Business Profile setup and audit", "Core on-page metadata and content fixes", "Essential local citation building and clean-up", "Technical health and indexing diagnostics", "Direct email communication with me"]
}, {
  id: "growth",
  name: "Growth SEO",
  badge: "Most Popular",
  tagline: "Ongoing optimisation and content expansion to win local search market share.",
  pricingLabel: "Custom pricing — get a free quote",
  isPopular: true,
  features: ["Everything in Essential SEO", "Ongoing target keyword and local landing page content", "Deeper technical SEO fixes and site speed tuning", "Customer review generation and management strategy", "Transparent monthly performance and ranking reports", "Bi-weekly check-ins and direct phone support"]
}, {
  id: "premium",
  name: "Premium SEO",
  tagline: "Full-scale organic search strategy with rapid iteration and dedicated access.",
  pricingLabel: "Custom pricing — get a free quote",
  isPopular: false,
  features: ["Everything in Growth SEO", "Advanced technical architecture and schema markup", "Competitor gap analysis and backlink acquisition strategy", "Full conversion tracking and analytics setup", "AI search visibility and brand citation optimisations", "Priority direct WhatsApp and phone access to me"]
}];
export default function Packages() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="packages" data-nav="light" className="bg-background text-foreground py-20 md:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-14 md:mb-20">
          <Badge variant="outline" className="mb-4 text-xs font-semibold uppercase tracking-wide border-primary/20 text-primary bg-primary/5 px-3 py-1 rounded-full">
            Direct Consulting Tiers
          </Badge>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground font-default">
            My Bedford SEO services and packages
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Choose the level of search support your business needs. You work directly with me on every strategy, audit, and fix — no account managers, juniors, or outsourced deliverables.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
          {packages.map((pkg, i) => <motion.div key={pkg.id} data-index={i} variants={cardVariants} className="flex">
              <Card className={`relative flex flex-col justify-between w-full rounded-2xl border transition-[border-color,box-shadow] duration-300 ${pkg.isPopular ? "border-primary bg-card shadow-lg ring-1 ring-primary/20" : "border-border bg-card/60 hover:border-border/80 shadow-xs hover:shadow-md"}`}>
                {pkg.isPopular && <div className="absolute -top-3.5 left-6">
                    <Badge variant="secondary" className="bg-accent text-accent-foreground font-semibold px-3 py-1 text-xs shadow-xs rounded-full flex items-center gap-1.5">
                      <Sparkles className="size-3.5" />
                      {pkg.badge}
                    </Badge>
                  </div>}

                <div>
                  <CardHeader className="pt-8 pb-4">
                    <CardTitle className="text-2xl font-bold tracking-tight text-foreground">
                      {pkg.name}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed min-h-[42px]">
                      {pkg.tagline}
                    </CardDescription>
                  </CardHeader>

                  <div className="px-6 py-4 my-2 border-y border-border/60 bg-muted/40">
                    <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground block mb-1">
                      Investment
                    </span>
                    <p className="text-base font-semibold text-foreground">
                      {pkg.pricingLabel}
                    </p>
                  </div>

                  <CardContent className="pt-6 pb-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
                      What is included:
                    </p>
                    <ul className="space-y-3.5 text-sm text-foreground/90">
                      {pkg.features.map((feature, featureIndex) => <li key={featureIndex} data-index={featureIndex} className="flex items-start gap-3">
                          <div className={`p-0.5 rounded-full shrink-0 mt-0.5 ${pkg.isPopular ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}`}>
                            <Check className="size-3.5 stroke-[2.5]" />
                          </div>
                          <span className="leading-snug">{feature}</span>
                        </li>)}
                    </ul>
                  </CardContent>
                </div>

                <CardFooter className="pt-6 pb-8">
                  <Button asChild variant={pkg.isPopular ? "default" : "outline"} size="lg" className="w-full justify-center group font-medium">
                    <Link to="/ContactPage">
                      <span>Book a Free SEO Consultation</span>
                      <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Bottom Trust Note */}
        <div className="mt-14 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left text-sm text-muted-foreground">
          <p>
            Need a bespoke audit or specialised AI search consultancy?
          </p>
          <Button asChild variant="link" className="p-0 h-auto font-medium text-primary">
            <Link to="/ContactPage">
              Discuss custom project requirements
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}